import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Checkbox, Dialog, IconButton, Skeleton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Header } from './CreateCodingInterview';
import { CaseMasterPublishingSteps } from 'components/PersonalizedInterview/constant';
import ProctorSettings from 'components/PersonalizedInterview/MockProctorSettings';
import SelectDatePicker, { SimpleSelect } from 'components/CustomSelectFields';
import TextField from 'components/CustomFields';
import CustomButton from 'components/CustomButtons';
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts';
import { getBatches } from 'services';
import { Session } from 'utils';
import { getCaseMasterTopics, getCaseMasterCases, createCaseMasterContest } from 'services/caseMasterService';

const useStyles = makeStyles((theme) => ({
  container: {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    padding: theme.spacing(10, 15),
    display: 'flex',
    gap: theme.spacing(2.5),
    flexDirection: 'column',
    width: '700px',
    background: '#FFFFFF',
    borderRadius: theme.spacing(2),
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                0px 10px 20px 0px #CCD4DE4D,
                0px 7px 28px 0px #CCD4DE99`,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
    marginTop: theme.spacing(3.5),
  },
}));

// MockProctorSettings labels its switches with its own key names, while the form
// state and the API both use the canonical proctoring keys. Translate between
// the two so the toggles reflect and persist the saved values.
const PROCTOR_KEY_BY_SWITCH = {
  prohibitCopying: 'copyPasteProctoring',
  prohibitTabSwitching: 'generalProctoring',
  enableImageCapturing: 'imageCaptureProctoring',
};

export const defaultProctorSettings = {
  copyPasteProctoring: true,
  generalProctoring: true,
  imageCaptureProctoring: false,
};

// A case master assignment has one flow; the attempt count is what decides
// whether it behaves as a contest (single shot) or a mock (repeatable).
// The interview portal derives it the same way from `maxAllowedAttempts`.
export const isCaseMasterContest = (maxAllowedAttempts) =>
  Number(maxAllowedAttempts) === 1;

const toProctorSwitches = (proctorSettings = {}) => ({
  prohibitCopying: proctorSettings.copyPasteProctoring ?? defaultProctorSettings.copyPasteProctoring,
  prohibitTabSwitching: proctorSettings.generalProctoring ?? defaultProctorSettings.generalProctoring,
  enableImageCapturing: proctorSettings.imageCaptureProctoring ?? defaultProctorSettings.imageCaptureProctoring,
});

const CaseMasterDetails = ({
  onNext,
  selectedTopic,
  setSelectedTopic,
  selectedCase,
  setSelectedCase,
}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [cases, setCases] = useState([]);
  const [autoAssign, setAutoAssign] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await service.callService(getCaseMasterTopics);
        const topicsArray = Object.values(data).map((t) => ({
          ...t,
          name: t.topic
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        }));
        setTopics(topicsArray);
      } catch (error) {
        snackbar.error('Unable to get case master topics.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedTopic?._id) {
      setCases([]);
      setSelectedCase(null);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const data = await service.callService(getCaseMasterCases, {
          topic: selectedTopic.topic,
        });
        const caseOptions = data.map((c) => ({ ...c, name: c.title }));
        setCases(caseOptions);

        if (autoAssign && caseOptions.length > 0) {
          const random = caseOptions[Math.floor(Math.random() * caseOptions.length)];
          setSelectedCase(random);
        }
      } catch (error) {
        snackbar.error('Unable to get case master cases.');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedTopic]);

  const handleTopicChange = useCallback((e) => {
    const topicId = e.target.value;
    const topic = topics.find((t) => t._id === topicId);
    setSelectedTopic(topic || null);
    setSelectedCase(null);
    setCases([]);
  }, [topics]);

  const handleCaseChange = useCallback((e) => {
    const caseId = e.target.value;
    const caseObj = cases.find((c) => c._id === caseId);
    setSelectedCase(caseObj || null);
  }, [cases]);

  const handleAutoAssignChange = useCallback((e) => {
    const checked = e.target.checked;
    setAutoAssign(checked);
    if (checked && cases.length > 0) {
      const random = cases[Math.floor(Math.random() * cases.length)];
      setSelectedCase(random);
    } else if (!checked) {
      setSelectedCase(null);
    }
  }, [cases]);

  return (
    <Box className={classes.formContainer}>
      {loading ? (
        <>
          <Skeleton height={48} width="100%" sx={{ borderRadius: 1 }} />
          <Skeleton height={48} width="100%" sx={{ borderRadius: 1 }} />
        </>
      ) : (
        <>
          <SimpleSelect
            label="Topic"
            value={selectedTopic?._id || ''}
            onChange={handleTopicChange}
            options={topics}
          />

          {selectedTopic && (
            <>
              {!autoAssign && (
                <SimpleSelect
                  label="Case"
                  value={selectedCase?._id || ''}
                  onChange={handleCaseChange}
                  options={cases}
                />
              )}
              <Box display="flex" alignItems="center" gap="16px">
                <Checkbox
                  checked={autoAssign}
                  onChange={handleAutoAssignChange}
                  sx={{
                    padding: 0,
                    color: '#003E8C',
                    '&.Mui-checked': { color: '#003E8C' },
                  }}
                />
                <Typography variant="body1">
                  Automatically assign a case based on the selected topic.
                </Typography>
              </Box>
            </>
          )}
        </>
      )}

      <CustomButton
        size="medium"
        onClick={onNext}
        disabled={!selectedTopic || !selectedCase}
      >
        Proceed
      </CustomButton>
    </Box>
  );
};

export const CaseMasterPublishing = ({ onFinish, initialValues, editPage = false }) => {
  const classes = useStyles();
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    getBatches({ client: Session.userId })
      .then(({ batches }) => setBatches(batches))
      .catch(console.error);
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Contest name is required'),
    batch: Yup.string().required('Please select a batch'),
    startAt: Yup.date().required('Start date is required'),
    endAt: Yup.date()
      .required('End date is required')
      .min(Yup.ref('startAt'), 'End date must be after start date'),
    maxAllowedAttempts: Yup.number()
      .typeError('Must be a number')
      .required('Number of attempts is required')
      .min(1, 'Minimum 1 attempt allowed')
      .integer('Only whole numbers are allowed'),
    proctorSettings: Yup.object({
      copyPasteProctoring: Yup.bool().optional().default(true),
      generalProctoring: Yup.bool().optional().default(true),
      imageCaptureProctoring: Yup.bool().optional().default(false),
    }).optional(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => onFinish && onFinish(values),
  });

  const handleDateChange = useCallback((name, date) => {
    formik.setFieldValue(name, date);
  }, [formik]);

  const handleProctorChange = useCallback((event) => {
    const switchName = event.target.name.replace('proctorSettings.', '');
    const key = PROCTOR_KEY_BY_SWITCH[switchName];
    if (key) formik.setFieldValue(`proctorSettings.${key}`, event.target.value);
  }, [formik]);

  const { batch, endAt, maxAllowedAttempts, name, startAt } = formik.values;
  const isDisabled = !formik.isValid || !batch || !endAt || !name || !startAt
    || !maxAllowedAttempts;

  return (
    <form className={classes.formContainer} onSubmit={formik.handleSubmit}>
      <TextField
        label="Contest name"
        name="name"
        placeholder="Enter contest name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        sx={{ width: '100%' }}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
      />

      <SimpleSelect
        label="Publishing in Batch"
        name="batch"
        options={batches}
        value={formik.values.batch}
        sx={{ width: '100%' }}
        onChange={formik.handleChange}
        error={formik.touched.batch && Boolean(formik.errors.batch)}
        helperText={formik.touched.batch && formik.errors.batch}
      />

      <Box display="flex" flexDirection="column" gap="8px" width="100%">
        <TextField
          label="Number of attempts user can take"
          name="maxAllowedAttempts"
          placeholder="Enter number of attempts"
          type="number"
          value={formik.values.maxAllowedAttempts}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          sx={{ width: '100%' }}
          inputProps={{ min: 1 }}
          error={formik.touched.maxAllowedAttempts && Boolean(formik.errors.maxAllowedAttempts)}
          helperText={formik.touched.maxAllowedAttempts && formik.errors.maxAllowedAttempts}
        />
        <Typography variant="body02-bold" sx={{ color: '#E2483C' }}>
          Set 1 attempt to create a contest, or more than 1 attempt to create a mock.
        </Typography>
      </Box>

      <SelectDatePicker
        name="startAt"
        label="Start At"
        value={formik.values.startAt}
        onChange={handleDateChange}
        disabled={false}
      />

      <SelectDatePicker
        name="endAt"
        label="End At"
        value={formik.values.endAt}
        onChange={handleDateChange}
        disabled={!formik.values.startAt}
        minDate={formik.values.startAt}
      />

      <ProctorSettings
        values={toProctorSwitches(formik.values.proctorSettings)}
        onChange={handleProctorChange}
        boxShadow="none"
      />

      <Box sx={{ marginTop: '20px', width: '100%' }}>
        <CustomButton
          variant="contained"
          type="submit"
          sx={{ width: '100%' }}
          disabled={isDisabled}
        >
          {editPage ? 'Save' : 'Publish Now'}
        </CustomButton>
      </Box>
    </form>
  );
};

const CreateCaseMasterContest = ({ open, onClose, setRefresh, currentStep, setCurrentStep }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);

  const steps = [
    CaseMasterPublishingSteps.CASE_MASTER_DETAILS,
    CaseMasterPublishingSteps.CASE_MASTER_PUBLISHING,
  ];

  const currentStepIndex = currentStep ? steps.indexOf(currentStep) : -1;

  const handleBack = () => {
    if (currentStepIndex <= 0) {
      setCurrentStep(null);
      onClose();
      return;
    }
    setCurrentStep(steps[currentStepIndex - 1]);
  };

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const handleFinish = async (values) => {
    try {
      const result = await service.callService(createCaseMasterContest, {
        client: Session.userId,
        batch: values.batch,
        name: values.name,
        casemasterProblem: selectedCase._id,
        startAt: values.startAt,
        endAt: values.endAt,
        maxAllowedAttempts: values.maxAllowedAttempts,
        proctorSettings: values.proctorSettings,
      });

      if (result) {
        setRefresh(true);
        snackbar.success(
          `Case Master ${isCaseMasterContest(values.maxAllowedAttempts) ? 'Contest' : 'Mock'} created successfully!`
        );
        setCurrentStep(null);
        onClose();
      }
    } catch (error) {
      snackbar.error(error?.message || 'Failed to create contest. Please try again.');
    }
  };

  const topicName = selectedTopic?.name
    ? selectedTopic.name.charAt(0).toUpperCase() + selectedTopic.name.slice(1)
    : '';
  const initialValues = {
    name: topicName && selectedCase?.title ? `${topicName} : ${selectedCase.title}` : '',
    batch: '',
    startAt: '',
    endAt: '',
    maxAllowedAttempts: '1',
    proctorSettings: { ...defaultProctorSettings },
  };

  const renderStep = () => {
    switch (currentStep) {
      case CaseMasterPublishingSteps.CASE_MASTER_DETAILS:
        return (
          <CaseMasterDetails
            onNext={handleNext}
            selectedTopic={selectedTopic}
            setSelectedTopic={setSelectedTopic}
            selectedCase={selectedCase}
            setSelectedCase={setSelectedCase}
          />
        );
      case CaseMasterPublishingSteps.CASE_MASTER_PUBLISHING:
        return (
          <CaseMasterPublishing
            onFinish={handleFinish}
            initialValues={initialValues}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="700px"
      width="100%"
      scroll="paper"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 17, 38, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          backgroundColor: 'white',
          '&:hover': { backgroundColor: '#f0f0f0' },
        }}
      >
        <CloseIcon />
      </IconButton>
      <Box display="flex" flexDirection="column">
        <Box className={classes.container}>
          <Box className={classes.box}>
            <Header onBack={handleBack} currentStep={currentStep} />
            {renderStep()}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateCaseMasterContest;
